import { reflect } from '@effector/reflect';
import { NumberFormatter, Switch } from '@mantine/core';
import { useUnit } from 'effector-react';
import { FileSearch } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TIME_ZONES } from 'shared/config';
import { translated } from 'shared/lib';
import {
	createDateFilter,
	createRegistry,
	RegistryRootProps,
	RegistryTableTfootProps,
} from 'shared/ui';
import { ShiftRegistryModel } from '../../model';
import cls from './shift-registry.module.scss';

export const createShiftRegistry = ({
	dateFilterModel,
	registryModel,
	$total,
}: ShiftRegistryModel) => {
	const Registry = createRegistry(registryModel);

	const DateFilter = createDateFilter(dateFilterModel);

	const PersonalField = reflect({
		view: Switch,
		bind: {
			className: cls.shiftRegistryPersonal,
			'data-test-id': 'shift-registry-personal',
			checked: registryModel.filters.personal.$value,
			onChange: (e) => registryModel.filters.personal.setValue(e.target.checked),
			label: translated('shift-registry.filters.personal'),
		},
	});

	const DateField = reflect({
		view: DateFilter.DateField,
		bind: {
			'data-test-id': 'shift-registry-date',
			className: cls.shiftRegistryDate,
		},
	});

	const TimeZoneField = reflect({
		view: DateFilter.TimeZoneField,
		bind: {
			'data-test-id': 'shift-registry-time-zone',
			className: cls.shiftRegistryTimeZone,
			value: registryModel.filters.timeZone.$value.map((tz) => tz.toString()),
			onChange: (value) =>
				registryModel.filters.timeZone.setValue(Number(value ?? TIME_ZONES['UTC-00:00'])),
		},
	});

	const ShiftRegistryTotal = forwardRef<HTMLTableSectionElement, RegistryTableTfootProps>(
		(props, ref) => {
			const { t } = useTranslation();

			const total = useUnit($total);

			if (!total) return null;

			return (
				<Registry.Table.Tfoot
					ref={ref}
					{...props}
				>
					<Registry.Table.Tr>
						<Registry.Table.Td colSpan={2}>
							{t('shift-registry.table-footer.total')}
						</Registry.Table.Td>
						<Registry.Table.Td>{total.currency}</Registry.Table.Td>
						<Registry.Table.Td>
							<NumberFormatter value={total.deposits} />
						</Registry.Table.Td>
						<Registry.Table.Td>
							<NumberFormatter value={total.withdrawals} />
						</Registry.Table.Td>
						<Registry.Table.Td>
							<NumberFormatter value={total.income} />
						</Registry.Table.Td>
					</Registry.Table.Tr>
				</Registry.Table.Tfoot>
			);
		},
	);

	const ShiftRegistry = forwardRef<HTMLDivElement, RegistryRootProps>((props, ref) => {
		const { t } = useTranslation();

		return (
			<Registry.Root
				ref={ref}
				{...props}
			>
				<Registry.TopBar alwaysShow>
					<Registry.Filters
						classNames={{
							inner: cls.shiftRegistryFiltersInner,
						}}
					>
						<div className={cls.shiftRegistryFiltersGroup}>
							<DateField />
							<TimeZoneField />
						</div>
						<PersonalField />
					</Registry.Filters>
				</Registry.TopBar>
				<Registry.Table>
					<Registry.Table.Colgroup>
						<Registry.Table.Col className={cls.shiftRegistryColDate} />
						<Registry.Table.Col className={cls.shiftRegistryColDate} />
						<Registry.Table.Col />
						<Registry.Table.Col />
						<Registry.Table.Col />
						<Registry.Table.Col />
					</Registry.Table.Colgroup>
					<Registry.Table.Thead>
						<Registry.Table.Tr>
							<Registry.Table.Th>{t('shift-registry.table-columns.start-time')}</Registry.Table.Th>
							<Registry.Table.Th>{t('shift-registry.table-columns.end-time')}</Registry.Table.Th>
							<Registry.Table.Th>{t('shift-registry.table-columns.currency')}</Registry.Table.Th>
							<Registry.Table.Th>{t('shift-registry.table-columns.deposits')}</Registry.Table.Th>
							<Registry.Table.Th>{t('shift-registry.table-columns.withdrawals')}</Registry.Table.Th>
							<Registry.Table.Th>{t('shift-registry.table-columns.income')}</Registry.Table.Th>
						</Registry.Table.Tr>
					</Registry.Table.Thead>
					<Registry.Table.Tbody>
						<Registry.Table.TrList>
							{(row) => (
								<>
									<Registry.Table.Td>{row.startTime}</Registry.Table.Td>
									<Registry.Table.Td>{row.endTime}</Registry.Table.Td>
									<Registry.Table.Td>{row.currency}</Registry.Table.Td>
									<Registry.Table.Td>
										<NumberFormatter value={row.deposits} />
									</Registry.Table.Td>
									<Registry.Table.Td>
										<NumberFormatter value={row.withdrawals} />
									</Registry.Table.Td>
									<Registry.Table.Td>
										<NumberFormatter value={row.income} />
									</Registry.Table.Td>
								</>
							)}
						</Registry.Table.TrList>
					</Registry.Table.Tbody>
					<ShiftRegistryTotal />
				</Registry.Table>
				<Registry.BottomBar>
					<Registry.Pagination />
				</Registry.BottomBar>
				<Registry.Empty
					icon={<FileSearch />}
					title={t('shift-registry.empty.title')}
					message={t('shift-registry.empty.message')}
				/>
				<Registry.Table.Empty
					icon={<FileSearch />}
					title={t('shift-registry.table-empty.title')}
					message={t('shift-registry.table-empty.message')}
				/>
			</Registry.Root>
		);
	});

	return ShiftRegistry;
};
